"use client";

import LeftQuote from "@/components/molecules/LeftQuote";
import { ProfileCard } from "@/components/molecules/ProfileCard";
import Carousel4 from "@/components/organisms/carousels/Carousel4";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import React, { useMemo, useRef, useState } from "react";

const OPTIONS: any = { loop: true };

// Mobile card width. The photo box is a fixed 200px tall with `object-contain`,
// so a portrait shot only ever fills ~150-270px of width — while the slide
// (76% of the viewport) and the coloured name block underneath stretch to the
// full slide. On wider phones that left the name block visibly broader than the
// photo it belongs to. Pinning the card and centring it in the slide keeps the
// block close to the photo instead of growing with the viewport.
const MOBILE_CARD_WIDTH = "w-[280px]";

interface MetaItem {
  id: string;
  meta_name: string;
  meta_designation: string;
  meta_status: boolean;
  meta_priority: number;
  meta_alt_image?: string;
  meta_mobile_image: string;
  meta_desktop_image: string;
  meta_name_text_color?: string;
  meta_name_background_color?: string;
  meta_designation_text_color?: string;
}

interface FacultyData {
  id: number;
  page_id: number;
  component_id: number;
  title: string;
  title_text_color?: string;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  description: string;
  description_text_color?: string;
  description_background_color?: string;
  metadata: MetaItem[];
  variant: string;
  status: number;
}

export interface FacultyProfile {
  id: string;
  name: string;
  position: string;
  quote?: string;
  featured?: boolean;
  image?: string;
  mobileImage?: string;
  nameBackgroundColor?: string;
  nameTextColor?: string;
  designationTextColor?: string;
}

export interface FacultyProps {
  title: string;
  profiles: FacultyProfile[];
}

const Faculty1: React.FC<{ data: { data: FacultyData } }> = ({ data }) => {
  const { data: sectionData } = data;
  const ref = useRef<HTMLElement>(null);

  const {
    title,
    title_text_color = "#000000",
    description = "",
    description_text_color = "#000000",
    description_background_color = "#EFF5F9",
    metadata = [],
    left_navigation_icon,
    left_navigation_icon_alt,
    right_navigation_icon,
    right_navigation_icon_alt,
  } = sectionData || {};

  const profiles: FacultyProfile[] = useMemo(
    () =>
      metadata
        ?.filter((item: MetaItem) => item.meta_status)
        ?.sort((a: MetaItem, b: MetaItem) => a.meta_priority - b.meta_priority)
        ?.map((item: MetaItem) => ({
          id: item.id,
          name: item.meta_name,
          position: item.meta_designation,
          quote: description,
          image: item.meta_desktop_image,
          mobileImage: item.meta_mobile_image,
          nameBackgroundColor: item.meta_name_background_color,
          nameTextColor: item.meta_name_text_color,
          designationTextColor: item.meta_designation_text_color,
        })) || [],
    [metadata, description],
  );

  // One index per layout. Both layouts are in the DOM (CSS shows one), so a shared
  // index would let swiping the visible carousel move the highlight in the hidden one.
  const [mobileActiveIndex, setMobileActiveIndex] = useState<number | null>(0);
  const [desktopActiveIndex, setDesktopActiveIndex] = useState<number | null>(0);

  // After every hook: returning before them changed the hook count between renders.
  if (!sectionData || !sectionData?.status) {
    return null;
  }

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "faculty_section");
  //   }
  // }, []);

  return (
    <section
      ref={ref}
      className={cn("w-full px-5 py-8", "sm:px-9 sm:py-11")}
      style={
        {
          "--title-color": title_text_color || "#000000",
          "--desc-bg-color": description_background_color || "#EFF5F9",
          "--desc-text-color": description_text_color || "#000000",
        } as React.CSSProperties
      }
    >
      <div className="flex flex-col items-center gap-6">
        {title && (
          <h2
            className={cn(
              "text-center text-heading-6-semibold",
              "sm:text-heading-5-semibold sm:max-w-[440px]",
              "md:text-heading-4-semibold md:max-w-[510px]",
              "lg:text-new-heading-34-semibold lg:max-w-[740px]",
              "xl:text-new-heading-42-semibold xl:max-w-[740px] ",
              "line-clamp-2",
              "type-h3",
            )}
            style={{ color: "var(--title-color)" }}
          >
            {title}
          </h2>
        )}

        <div className="flex flex-col items-center w-full gap-6">
          {description && (
            <div
              className={cn(
                "bg-primary-2 border border-primary-3 rounded-xl w-full p-[10px] ",
                "sm:p-2 sm:max-w-[440px] ",
                "md:p-3 md:max-w-[510px] ",
                "lg:p-6 lg:max-w-[740px] ",
                "xl:p-6 xl:max-w-[740px] ",
                "line-clamp-2",
              )}
              style={{ backgroundColor: "var(--desc-bg-color)" }}
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <div className="flex-shrink-0 mt-1 self-start">
                  <LeftQuote className="w-4 h-4 text-primary-main" />
                </div>
                <div
                  className={cn(
                    "text-body-3-regular",
                    "md:text-body-2-regular",
                    "lg:text-new-body-24-regular",
                    "self-center",
                    "line-clamp-2",
                  )}
                  style={{ color: "var(--desc-text-color)" }}
                >
                  {description}
                </div>
              </div>
            </div>
          )}

          {/*
           * Both layouts are rendered and CSS picks one at 800px — the same cut-off the
           * old useIsMobile(800) used. That hook has no value on the server, so the
           * server HTML always carried the desktop layout and phones swapped to the
           * carousel only after hydration. Hidden cards' images are lazy and never load.
           */}
          <section className="w-screen max-w-[100vw] overflow-hidden min-[800px]:hidden">
            <Carousel4
              options={OPTIONS}
              onSlideChange={setMobileActiveIndex}
              left_navigation_icon={left_navigation_icon}
              left_navigation_icon_alt={left_navigation_icon_alt}
              right_navigation_icon={right_navigation_icon}
              initialIndex={0}
            >
              {profiles.map((profile, index) => (
                <div
                  key={profile.id}
                  className={cn("mx-auto max-w-full", MOBILE_CARD_WIDTH)}
                >
                  <ProfileCard
                    profile={profile}
                    index={index}
                    isActive={index === mobileActiveIndex}
                  />
                </div>
              ))}
            </Carousel4>
          </section>

          {profiles.length > 5 ? (
            <div
              className={cn(
                "flex items-center justify-center w-full",
                "sm:max-w-[1080px]",
                "md:max-w-[1230px]",
                "lg:max-w-[1430px]",
                "xl:max-w-[1750px]",
                "max-[800px]:hidden",
              )}
            >
              <Carousel4
                options={OPTIONS}
                onSlideChange={setDesktopActiveIndex}
                left_navigation_icon={left_navigation_icon}
                left_navigation_icon_alt={left_navigation_icon_alt}
                right_navigation_icon={right_navigation_icon}
                right_navigation_icon_alt={right_navigation_icon_alt}
                initialIndex={0}
              >
                {profiles.map((profile, index) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    index={index}
                    isActive={index === desktopActiveIndex}
                  />
                ))}
              </Carousel4>
            </div>
          ) : (
            // 5 or fewer profiles fit within the viewport, so render them as a
            // static centered row instead of a carousel (no navigation, every
            // card fully shown).
            <div
              className={cn(
                "flex items-center justify-center w-full",
                "sm:max-w-[1080px]",
                "md:max-w-[1230px]",
                "lg:max-w-[1430px]",
                "xl:max-w-[1750px]",
                "max-[800px]:hidden",
              )}
            >
              <div className="flex justify-center gap-[24px] w-full py-3">
                {profiles.map((profile, index) => (
                  <div
                    key={profile.id}
                    className="flex items-center justify-center flex-[0_0_calc(20%_-_19.2px)]"
                  >
                    <div className="w-full">
                      <ProfileCard
                        profile={profile}
                        index={index}
                        isActive={true}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Faculty1;
